import { useState, useRef, useEffect } from 'react'
import { useDashReport } from '../hooks/useDashReport'
import type { MatchReport, CiudadStat } from '../types'

interface NerasildaReport extends MatchReport {
  google_sheets_url: string
  ciudades: CiudadStat[]
}

function getCiudadBadge(estado: string) {
  if (estado === 'Alto Match' || estado === 'Excelente') return 'bg-green-100 text-green-800'
  if (estado === 'Bajo Match') return 'bg-yellow-100 text-yellow-800'
  return 'bg-red-100 text-red-800'
}

export function NerasildaTadanerfilPage() {
  const { data, isLoading, error } = useDashReport<NerasildaReport>('odt/nerasilda-tadanerfil')
  const [ciudadesExpanded, setCiudadesExpanded] = useState(false)
  const ciudadesContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (ciudadesContentRef.current) {
      if (ciudadesExpanded) {
        ciudadesContentRef.current.style.maxHeight = ciudadesContentRef.current.scrollHeight + 'px'
        ciudadesContentRef.current.style.opacity = '1'
      } else {
        ciudadesContentRef.current.style.maxHeight = '0px'
        ciudadesContentRef.current.style.opacity = '0'
      }
    }
  }, [ciudadesExpanded])

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-gray-100 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">Análisis de Cobertura - ODT</h1>
                <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
              </div>
            </div>
            <div className="flex items-center">
              <a
                href={data.google_sheets_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-2 lg:px-4 lg:py-2 bg-green-600 hover:bg-green-700 text-white text-xs lg:text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm w-full lg:w-auto justify-center"
              >
                <svg className="w-3 h-3 lg:w-4 lg:h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 2h8v2H6V6zm0 4h8v2H6v-2zm0 4h4v2H6v-2z" />
                </svg>
                Ver Base de Datos
              </a>
            </div>
          </div>
        </div>

        {/* Estadisticas Generales */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Resumen General
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-900">{data.total_medicos}</p>
              <p className="text-sm text-gray-600 mt-1">Total Médicos</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-green-600">{data.con_coincidencia}</p>
              <p className="text-sm text-gray-600 mt-1">Con Correo</p>
              <p className="text-xs text-green-600 font-semibold mt-1">{data.porcentaje_match}%</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-500">{data.sin_coincidencia}</p>
              <p className="text-sm text-gray-600 mt-1">Sin Correo</p>
              <p className="text-xs text-gray-500 font-semibold mt-1">{data.porcentaje_sin_match}%</p>
            </div>
          </div>
        </div>

        {/* Desglose por Ciudad */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setCiudadesExpanded(!ciudadesExpanded)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-indigo-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-indigo-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-base lg:text-lg">Distribución por Ciudad</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">({data.ciudades.length} ciudades)</span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${ciudadesExpanded ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          <div className="mt-4" />

          <div
            ref={ciudadesContentRef}
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{ maxHeight: 0, opacity: 0 }}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ciudad</th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Con Correo</th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Sin Correo</th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <span className="hidden sm:inline">% Correo</span>
                      <span className="sm:hidden">Correo</span>
                    </th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.ciudades.map((ciudad) => (
                    <tr key={ciudad.ciudad} className="hover:bg-gray-50">
                      <td className="px-3 lg:px-6 py-4 text-sm font-medium text-gray-900">
                        <div className="whitespace-nowrap">{ciudad.ciudad}</div>
                        <div className="text-xs text-gray-500 sm:hidden">Total: {ciudad.total}</div>
                      </td>
                      <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">{ciudad.total}</td>
                      <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center text-green-600 font-semibold hidden sm:table-cell">{ciudad.con_match}</td>
                      <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500 hidden sm:table-cell">{ciudad.sin_match}</td>
                      <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center">
                        <span className="text-blue-600 font-bold">{ciudad.porcentaje}%</span>
                        <div className="text-xs text-gray-500 sm:hidden">
                          <span className="text-green-600">{ciudad.con_match}</span>/<span className="text-gray-500">{ciudad.sin_match}</span>
                        </div>
                      </td>
                      <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-center">
                        <span className={`px-1 lg:px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getCiudadBadge(ciudad.estado)}`}>
                          {ciudad.estado}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {/* Total row */}
                  <tr className="bg-gray-100 font-semibold">
                    <td className="px-3 lg:px-6 py-4 text-sm text-gray-900">
                      <div className="whitespace-nowrap">TOTAL</div>
                      <div className="text-xs text-gray-500 sm:hidden">Total: {data.total_medicos}</div>
                    </td>
                    <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">{data.total_medicos}</td>
                    <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center text-green-600 hidden sm:table-cell">{data.con_coincidencia}</td>
                    <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500 hidden sm:table-cell">{data.sin_coincidencia}</td>
                    <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center">
                      <span className="text-blue-600 font-bold">{data.porcentaje_match}%</span>
                      <div className="text-xs text-gray-500 sm:hidden">
                        <span className="text-green-600">{data.con_coincidencia}</span>/<span className="text-gray-500">{data.sin_coincidencia}</span>
                      </div>
                    </td>
                    <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-center">-</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Analisis por Ciudad */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Grafica por Ciudad (2/3 width) */}
          <div className="lg:col-span-2 bg-blue-50 rounded-lg p-6 border border-blue-200">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Cobertura de Correos por Ciudad
            </h3>
            <p className="text-gray-600 text-sm mb-4">Distribución de médicos con correo electrónico según ciudad.</p>

            <div className="space-y-3">
              {data.ciudades.map((ciudad) => (
                <div key={ciudad.ciudad} className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="flex flex-col space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 mr-4">
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="font-medium text-gray-900">{ciudad.ciudad}</span>
                          <span className="text-xs text-gray-500 sm:ml-2">{ciudad.total} médicos totales</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        <span className="text-green-600 font-semibold">{ciudad.con_match}</span>
                        <span className="text-gray-500 text-sm">/</span>
                        <span className="text-gray-700">{ciudad.total}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${ciudad.porcentaje}%` }} />
                      </div>
                      <span className="font-bold text-blue-600">{ciudad.porcentaje}%</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <div className="flex space-x-3">
                        <span><span className="text-green-600 font-semibold">{ciudad.con_match}</span> con correo</span>
                        <span><span className="text-gray-500 font-semibold">{ciudad.sin_match}</span> sin correo</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar - Resumen Total */}
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 shadow-sm h-full flex flex-col justify-center">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center text-sm">
              <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Cobertura Total
            </h3>

            <div className="text-center py-4">
              {/* Grafico de acelerador con numero en el centro */}
              <div className="relative w-32 h-32 mx-auto mb-4">
                <svg className="w-32 h-32" viewBox="0 0 100 100">
                  {/* Arco de fondo (gris) */}
                  <path d="M 20 50 A 30 30 0 0 1 80 50" stroke="#e5e7eb" strokeWidth="8" fill="none" strokeLinecap="round" />
                  {/* Arco de progreso (azul) */}
                  <path d={`M 20 50 A 30 30 0 0 1 ${data.acelerador_x} ${data.acelerador_y}`} stroke="#3b82f6" strokeWidth="8" fill="none" strokeLinecap="round" />
                </svg>

                {/* Numero en el centro */}
                <div className="absolute inset-0 flex items-center justify-center mt-6">
                  <div className="text-4xl font-bold text-blue-600">{data.con_coincidencia}</div>
                </div>
              </div>

              {/* Porcentaje */}
              <div className="text-base font-medium text-blue-400 mb-2">
                {Number(data.porcentaje_match).toFixed(2)}%
              </div>

              {/* Label */}
              <div className="flex items-center justify-center text-sm text-gray-600">
                <svg className="w-4 h-4 text-blue-600 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Con Correo Electrónico
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer sutil */}
      <footer className="mt-12 py-6 text-center">
        <div className="text-xs text-gray-400">
          <a href="https://enterprise.tonalstrategies.com" className="hover:text-gray-600 transition-colors">
            Tonal Enterprise
          </a>
          <span className="mx-2">&bull;</span>
          <span>&copy; 2025 Tonal Strategies</span>
        </div>
      </footer>

      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.1); opacity: 1; }
        }
      `}</style>
    </div>
  )
}
